import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  IconButton,
  Box,
  Stack,
  Switch,
  Divider,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

const AutoFillSettingsModal = ({
  open,
  onClose,
  fieldTitle,
  value,
  formProperties,
  onSave,
}) => {
  const [type, setType] = useState('none');
  const [defaultValue, setDefaultValue] = useState('');
  const [options, setOptions] = useState(['']);
  const [isRequired, setIsRequired] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (value?.type === 'options') {
      setType('options');
      setOptions(value.options?.length ? [...value.options] : ['']);
      setDefaultValue('');
    } else if (value?.type === 'default') {
      setType('default');
      setDefaultValue(value.defaultValue ?? '');
      setOptions(['']);
    } else {
      setType('none');
      setDefaultValue('');
      setOptions(['']);
    }
    setIsRequired(Boolean(formProperties?.isRequired));
  }, [open, value, formProperties]);

  const handleOptionChange = (index, newValue) => {
    setOptions((prev) => prev.map((opt, i) => (i === index ? newValue : opt)));
  };

  const handleAddOption = () => {
    setOptions((prev) => [...prev, '']);
  };

  const handleRemoveOption = (index) => {
    setOptions((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [''];
    });
  };

  const handleSave = () => {
    let nextAutoFill;
    if (type === 'none') {
      nextAutoFill = undefined;
    } else if (type === 'default') {
      const trimmed = defaultValue.trim();
      nextAutoFill =
        trimmed === '' ? undefined : { type: 'default', defaultValue };
    } else {
      const filtered = Array.from(
        new Set(options.map((o) => o.trim()).filter(Boolean)),
      );
      nextAutoFill =
        filtered.length === 0
          ? undefined
          : { type: 'options', options: filtered };
    }
    const nextFormProperties = isRequired ? { isRequired: true } : undefined;
    onSave({ autoFill: nextAutoFill, formProperties: nextFormProperties });
  };

  const handleClear = () => {
    onSave({ autoFill: undefined, formProperties: undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        Field Settings
        {fieldTitle && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {fieldTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <FormControlLabel
          control={
            <Switch
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
          }
          label='Required'
        />
        <Divider sx={{ my: 2 }} />
        <FormControl>
          <FormLabel>Auto-fill type</FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value)}>
            <FormControlLabel value='none' control={<Radio />} label='None' />
            <FormControlLabel
              value='default'
              control={<Radio />}
              label='Default'
            />
            <FormControlLabel
              value='options'
              control={<Radio />}
              label='Options'
            />
          </RadioGroup>
        </FormControl>

        {type !== 'none' && (
          <Box sx={{ mt: 2 }}>
            {type === 'default' ? (
              <TextField
                fullWidth
                size='small'
                label='Default value'
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
              />
            ) : (
              <Stack spacing={1}>
                {options.map((opt, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      size='small'
                      label={`Option ${index + 1}`}
                      value={opt}
                      onChange={(e) =>
                        handleOptionChange(index, e.target.value)
                      }
                    />
                    <IconButton
                      aria-label='Remove option'
                      size='small'
                      onClick={() => handleRemoveOption(index)}>
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Box>
                ))}
                <Box>
                  <Button
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={handleAddOption}>
                    Add option
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          color='error'
          onClick={handleClear}
          disabled={!value && !formProperties}>
          Clear settings
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ color: '#fff' }}>
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AutoFillSettingsModal;
